import axios from 'axios';
import { showToast } from '@/mixins/fcgeral';

// ─── Instância axios ──────────────────────────────────────────────────────────
const api = axios.create({
    baseURL: process.env.VUE_APP_BASE_URL + '/'
});

// Injeta token JWT automaticamente em todas as requisições
api.interceptors.request.use(config => {
    const user = JSON.parse(localStorage.getItem('userDados') || 'null');
    if (user?.token) {
        config.headers['Authorization'] = 'Bearer ' + user.token;
    }
    // Multi-CNPJ: contextualiza o request na empresa ativa (filha selecionada no menu superior)
    try {
        const empresa = JSON.parse(localStorage.getItem('empresaAtiva') || 'null');
        if (empresa?.cnpj && empresa.cnpj !== user?.cnpj) {
            config.headers['X-Empresa-Cnpj'] = empresa.cnpj;
        }
    } catch { /* seleção corrompida é ignorada */ }
    config.headers['Access-Control-Expose-Headers'] = '*';
    return config;
}, error => Promise.reject(error));

// Redireciona para logout automaticamente em caso de 401
api.interceptors.response.use(
    response => response,
    error => {
        if (error.response?.status === 401) {
            window.location.href = '/auth/logout';
        }
        // Multi-CNPJ: vínculo da empresa ativa inválido/removido — volta ao titular sem deslogar
        if (error.response?.status === 403 && error.response?.data?.msg === 'empresa_invalida') {
            localStorage.removeItem('empresaAtiva');
            showToast('error', 'A empresa selecionada não está mais vinculada à sua conta. Voltando para a empresa titular.');
            setTimeout(() => window.location.assign('/'), 1500);
        }
        return Promise.reject(error);
    }
);

// ─── Envelope padrão ──────────────────────────────────────────────────────────
// Todos os retornos seguem: { ok: boolean, msg: string, data: any }
// error: código do erro de negócio vindo da API (ex.: 'sem_licenca'), para o chamador poder reagir
const fail = (msg, error = undefined) => ({ ok: false, msg, data: null, error });

/**
 * Extrai envelope padronizado de uma resposta de erro axios.
 * Erros de negócio (400) e servidor (500) retornam { ok: false, msg, error, traceId }.
 * Erros de rede não têm response.
 */
const trataErro = (error) => {
    // Servidor respondeu com status de erro (400, 500, etc.)
    const respData = error.response?.data;
    if (respData && respData.ok === false) {
        return fail(respData.msg || 'Erro na operação.', respData.error);
    }
    // Mensagem genérica do servidor (formato antigo ou inesperado)
    if (respData?.message) {
        return fail(respData.message);
    }
    // ValidationProblem/ProblemDetails do ASP.NET (ex.: falha de model binding).
    // Sem este tratamento o usuário vê o "Request failed with status code 400"
    // cru do axios, sem nenhuma pista do campo problemático.
    if (respData?.errors && typeof respData.errors === 'object') {
        const detalhes = Object.entries(respData.errors)
            .map(([campo, msgs]) => `${campo}: ${[].concat(msgs).join(' ')}`)
            .join(' | ');
        return fail(`Dados inválidos na requisição. ${detalhes}`);
    }
    if (respData?.title) {
        return fail(respData.title);
    }
    // Erro de rede / timeout
    if (error.message === 'Network Error') {
        return fail('Não foi possível conectar ao servidor!');
    }
    return fail(error.message || 'Ocorreu um erro na operação, tente novamente ou contate o suporte técnico!');
};

// ─── Funções de API ───────────────────────────────────────────────────────────

// GET simples
// silent=true suprime o toast automático de erro — útil quando o chamador já
// trata a falha (ex.: recarregar itens sem sobrescrever os já exibidos).
export async function apiGet(url, { silent = false } = {}) {
    try {
        const { data } = await api.get(url);
        return data; // { ok, msg, data }
    } catch (error) {
        const resp = trataErro(error);
        if (!silent) showToast('error', resp.msg);
        return resp;
    }
}

// POST com JSON
// silent=true (opcional; padrão false = comportamento de sempre) suprime o toast de erro — quem chama
// mostra a falha do seu jeito (ex.: sugestão de produto por item, que marca a célula em vez de um toast).
export async function apiPost(url, dados = {}, { silent = false } = {}) {
    try {
        const { data } = await api.post(url, dados);
        return data; // { ok, msg, data }
    } catch (error) {
        const resp = trataErro(error);
        if (!silent) showToast('error', resp.msg);
        return resp;
    }
}

// POST com arquivo (multipart/form-data)
export async function apiPostFile(url, formData, onProgress = null) {
    const config = { headers: { 'Content-Type': 'multipart/form-data' } };
    if (onProgress) config.onUploadProgress = onProgress;
    try {
        const { data } = await api.post(url, formData, config);
        return data;
    } catch (error) {
        const resp = trataErro(error);
        showToast('error', resp.msg);
        return resp;
    }
}

// Com responseType 'blob' o corpo de um erro 4xx/5xx também chega como Blob. Se for o JSON
// padrão da API ({ ok:false, msg }), troca por objeto para o trataErro mostrar a msg real
// em vez do "Request failed with status code 4xx" do axios. Só mexe no caminho de erro.
const leCorpoDeErroBlob = async (error) => {
    const corpo = error?.response?.data;
    if (!(corpo instanceof Blob) || !(corpo.type || '').includes('application/json')) return;
    try {
        error.response.data = JSON.parse(await corpo.text());
    } catch { /* corpo ilegível: mantém o erro genérico do axios */ }
};

// Download de arquivo (blob)
export async function apiDownload(url, nomeArquivo = '') {
    try {
        const response = await api.get(url, { responseType: 'blob' });

        const blob = new Blob([response.data], { type: response.data.type });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;

        // Extrai nome do arquivo do cabeçalho Content-Disposition
        const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'] || '';
        let fileName = nomeArquivo;
        if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            if (match?.[1]) fileName = match[1];
            // Nome com acento: o ASP.NET manda também filename*=UTF-8''... (RFC 5987), que vale
            // mais que o filename simples (este troca os acentos por "_").
            const matchUtf8 = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
            if (matchUtf8?.[1]) {
                try { fileName = decodeURIComponent(matchUtf8[1].trim()); } catch { /* mantém o filename simples */ }
            }
        }

        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        return { ok: true, msg: '', data: response.data };
    } catch (error) {
        await leCorpoDeErroBlob(error);
        const resp = trataErro(error);
        showToast('error', resp.msg);
        return resp;
    }
}

// GET que devolve o arquivo em memória (Blob + nome), SEM salvar no computador do usuário.
// Usado quando o arquivo segue para outro lugar (ex.: ficha técnica do produto → proposta do portal).
// silent=true não mostra o toast de erro (o chamador conta o que houve na mensagem dele).
export async function apiGetBlob(url, { silent = false } = {}) {
    try {
        const response = await api.get(url, { responseType: 'blob' });
        const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'] || '';
        let nome = '';
        const match = contentDisposition.match(/filename="?([^";]+)"?/);
        if (match?.[1]) nome = match[1];
        // nome com acento: filename*=UTF-8''... (RFC 5987) vale mais, como no apiDownload
        const matchUtf8 = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (matchUtf8?.[1]) {
            try { nome = decodeURIComponent(matchUtf8[1].trim()); } catch { /* mantém o filename simples */ }
        }
        return { ok: true, msg: '', data: { blob: response.data, nome } };
    } catch (error) {
        await leCorpoDeErroBlob(error);
        const resp = trataErro(error);
        if (!silent) showToast('error', resp.msg);
        return resp;
    }
}

// Download de arquivo via POST (envia JSON, recebe blob)
export async function apiPostDownload(url, dados = {}, nomeArquivo = '') {
    try {
        const response = await api.post(url, dados, { responseType: 'blob' });

        // Erro da API vem como JSON dentro do blob — detecta e converte
        if ((response.data.type || '').includes('application/json')) {
            const texto = await response.data.text();
            const json = JSON.parse(texto);
            if (!json.ok) {
                showToast('error', json.msg || 'Erro ao gerar o arquivo.');
                return json;
            }
        }

        const blob = new Blob([response.data], { type: response.data.type });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;

        const contentDisposition = response.headers['content-disposition'] || '';
        let fileName = nomeArquivo;
        const match = contentDisposition.match(/filename="?([^";]+)"?/);
        if (match?.[1]) fileName = match[1];

        link.setAttribute('download', fileName || 'arquivo');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        return { ok: true, msg: '', data: null };
    } catch (error) {
        const resp = trataErro(error);
        showToast('error', resp.msg);
        return resp;
    }
}

// POST que recebe blob e devolve uma object URL (para abrir em nova aba, sem baixar)
export async function apiPostBlob(url, dados = {}) {
    try {
        const response = await api.post(url, dados, { responseType: 'blob' });

        // Erro da API vem como JSON dentro do blob — detecta e converte
        if ((response.data.type || '').includes('application/json')) {
            const texto = await response.data.text();
            const json = JSON.parse(texto);
            if (!json.ok) {
                showToast('error', json.msg || 'Erro ao gerar o arquivo.');
                return json;
            }
        }

        const blob = new Blob([response.data], { type: response.data.type });
        return { ok: true, msg: '', data: { blobUrl: window.URL.createObjectURL(blob) } };
    } catch (error) {
        const resp = trataErro(error);
        showToast('error', resp.msg);
        return resp;
    }
}

// Compatibilidade retroativa — mantida para não quebrar código existente
export default async function connServ(url, tpreq, dados, _router, onUploadProgress = null) {
    if (tpreq === 'get') return apiGet(url);
    if (tpreq === 'post') return apiPost(url, dados);
    if (tpreq === 'postFile') return apiPostFile(url, dados, onUploadProgress);
    if (tpreq === 'down') return apiDownload(url, dados);
    return fail('Tipo de requisição não suportado.');
}
